import { memo, useEffect, useRef, useState } from 'react';
import type { PreisToolOpenPriceChange } from '@/api/preis-tool/preis-tool.types';
import { fmtEUR, fmtPct, marginPct, marginClass, openChangeText } from '../../helpers';

export type CellNavDirection = 'down' | 'right' | 'up' | 'left' | 'cancel';

interface EditablePriceCellProps {
  price: number | null;
  mek: number;
  draftPrice?: number | null;
  onCommit: (value: number | null) => void;
  triggerEdit?: boolean;
  initialKey?: string;
  onNav?: (dir: CellNavDirection) => void;
  /** When false, the cell is read-only — double-click / keyboard edit are inert. */
  canEdit?: boolean;
  /** Saved change that is not in SAP yet, or whose transfer failed. */
  openChange?: PreisToolOpenPriceChange;
  /** "Niedrige Marge" limit of the legend (EOS-61); default 5 %. */
  lowMarginPct?: number;
}

function parsePrice(s: string): number | null {
  const cleaned = s.replace(',', '.').trim();
  if (cleaned === '') return null;
  const n = parseFloat(cleaned);
  return isNaN(n) ? null : +n.toFixed(2);
}

export const EditablePriceCell = memo(function EditablePriceCell({
  price,
  mek,
  draftPrice,
  onCommit,
  triggerEdit,
  initialKey,
  onNav,
  canEdit = true,
  openChange,
  lowMarginPct,
}: EditablePriceCellProps) {
  const displayPrice = draftPrice !== undefined ? draftPrice : price;
  const isDirty = draftPrice !== undefined && draftPrice !== price;

  const [editing, setEditing] = useState(false);
  const [inputVal, setInputVal] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const livePrice = editing ? parsePrice(inputVal) : displayPrice;
  const margin = marginPct(livePrice, mek);
  const cls = marginClass(margin, lowMarginPct);

  useEffect(() => {
    if (canEdit && triggerEdit && !editing) setEditing(true);
  }, [triggerEdit]);

  useEffect(() => {
    if (editing) {
      if (initialKey) {
        setInputVal(initialKey);
        setTimeout(() => {
          inputRef.current?.focus();
          inputRef.current?.setSelectionRange(initialKey.length, initialKey.length);
        });
      } else {
        setInputVal(displayPrice != null ? displayPrice.toFixed(2) : '');
        setTimeout(() => {
          inputRef.current?.focus();
          inputRef.current?.select();
        });
      }
    }
  }, [editing]);

  const commit = (s: string, nav?: CellNavDirection) => {
    const val = parsePrice(s);
    onCommit(val);
    setEditing(false);
    if (nav) onNav?.(nav);
  };

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        width: '100%',
        justifyContent: 'flex-end',
        cursor: canEdit ? 'pointer' : 'default',
      }}
      onDoubleClick={() => { if (canEdit && !editing) setEditing(true); }}
    >
      {openChange && !editing && (
        <span
          className={`ds-dot ${openChange.status === 'FAILED' ? 'critical' : 'warn'}`}
          title={openChangeText(openChange)}
          style={{ flexShrink: 0 }}
        />
      )}
      {editing ? (
        <input
          ref={inputRef}
          type="text"
          value={inputVal}
          onChange={(e) => setInputVal(e.target.value)}
          onBlur={(e) => commit(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); commit((e.target as HTMLInputElement).value, e.shiftKey ? 'up' : 'down'); }
            if (e.key === 'Tab') { e.preventDefault(); commit((e.target as HTMLInputElement).value, e.shiftKey ? 'left' : 'right'); }
            if (e.key === 'ArrowDown') { e.preventDefault(); commit((e.target as HTMLInputElement).value, 'down'); }
            if (e.key === 'ArrowUp') { e.preventDefault(); commit((e.target as HTMLInputElement).value, 'up'); }
            if (e.key === 'Escape') { setEditing(false); onNav?.('cancel'); }
          }}
          style={{
            border: 'none',
            outline: 'none',
            background: 'transparent',
            font: 'inherit',
            fontFamily: 'var(--font-mono)',
            fontSize: 'var(--fs-sm)',
            textAlign: 'right',
            width: '100%',
            flex: 1,
            minWidth: 0,
            padding: 0,
            color: 'var(--ink)',
          }}
        />
      ) : (
        <span style={{ flex: 1, textAlign: 'right', color: livePrice == null ? 'var(--ink-3)' : undefined }}>
          {livePrice != null ? fmtEUR(livePrice) : '—'}
        </span>
      )}
      <span
        className={`ds-pill ${cls}`}
        style={{ width: 40, height: 15.5, justifyContent: 'center', display: 'inline-flex', flexShrink: 0 }}
      >
        {margin != null ? (margin >= 0 ? '+' : '') + fmtPct(margin, 0) : '—'}
      </span>
    </span>
  );
});
